import React, { useContext, useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { get as apiGet, post as apiPost } from '@utils/axios_api';
import { AuthContext } from '@context';
import { LoginModal } from '@components';

const BOOT_URL = process.env.REACT_APP_BOOT_URL || '';

// 공개 엔드포인트 — 인증 불필요, raw axios 사용
const fetchSharedCard = async (token) => {
    const res = await axios.get(`${BOOT_URL}/api/share/${token}`);
    return res.data;
};

// 인증 필요 엔드포인트 — axios_api 인터셉터로 JWT 헤더 자동 포함
const checkSaved = async (cardId) => {
    try {
        const data = await apiGet(`/biz/cards/${cardId}/save`, { suppressToast: true });
        return data?.result === true;
    } catch {
        return false;
    }
};

const doSaveCard = async (cardId) => {
    return apiPost(`/biz/cards/${cardId}/save`, {});
};

const FIELD_LABELS = [
    { key: 'person_name',  label: '이름' },
    { key: 'company_name', label: '회사명' },
    { key: 'job_title',    label: '직함' },
    { key: 'phone',        label: '전화번호' },
    { key: 'email',        label: '이메일' },
    { key: 'address',      label: '주소' },
    { key: 'website',      label: '웹사이트' },
];

function parseJsonField(raw) {
    if (!raw) return null;
    if (typeof raw === 'string') { try { return JSON.parse(raw); } catch { return null; } }
    return raw;
}

function getFields(card) {
    const finalJson = parseJsonField(card?.admin_final_json);
    const llmJson   = parseJsonField(card?.llm_structured_json);
    const apiFields = finalJson?.fields ?? llmJson?.fields ?? [];
    const map = {};
    for (const f of apiFields) {
        if (f?.key && f?.value) map[f.key] = f.value;
    }
    return map;
}

const SAVE_STATE = {
    CHECKING: 'checking',
    IDLE:     'idle',
    LOADING:  'loading',
    SAVED:    'saved',
    ALREADY:  'already',
    NO_AUTH:  'no_auth',
    ERROR:    'error',
};

export function BizCardSharePage() {
    const { token } = useParams();
    const { auth } = useContext(AuthContext);
    const isLoggedIn = !!auth?.isAuthenticated;

    const [loginModalOpen, setLoginModalOpen] = useState(false);
    const [saveState, setSaveState] = useState(isLoggedIn ? SAVE_STATE.CHECKING : SAVE_STATE.NO_AUTH);
    // 모달 로그인 직후 자동저장 중임을 표시 — isLoggedIn 변화로 재실행되는 effect의 중복 체크를 막는다
    const autoSavingRef = useRef(false);

    const handleLoginClick = () => setLoginModalOpen(true);

    const { data, isLoading, isError } = useQuery({
        queryKey: ['bizShare', token],
        queryFn: () => fetchSharedCard(token),
        enabled: !!token,
        retry: false,
        staleTime: 60_000,
    });

    const cardId  = data?.id ?? null;
    const fields  = getFields(data);
    const imageUrl = data?.image_url ? `${BOOT_URL}/api${data.image_url}` : null;

    // 카드 로드 후 로그인 상태면 이미 저장됐는지 확인
    useEffect(() => {
        if (!cardId) return;
        if (!isLoggedIn) {
            setSaveState(SAVE_STATE.NO_AUTH);
            return;
        }
        // 모달 로그인 직후엔 saveCard()가 저장을 처리하므로 중복 GET 체크 생략
        if (autoSavingRef.current) {
            autoSavingRef.current = false;
            return;
        }
        setSaveState(SAVE_STATE.CHECKING);
        checkSaved(cardId).then((saved) =>
            setSaveState(saved ? SAVE_STATE.ALREADY : SAVE_STATE.IDLE)
        );
    }, [cardId, isLoggedIn]);

    const saveCard = async () => {
        if (!cardId) return;
        setSaveState(SAVE_STATE.LOADING);
        try {
            const res = await doSaveCard(cardId);
            const alreadySaved = res?.status === false || res?.message?.includes('이미');
            if (alreadySaved) {
                setSaveState(SAVE_STATE.ALREADY);
            } else {
                setSaveState(SAVE_STATE.SAVED);
            }
        } catch (err) {
            const status = err?.response?.status;
            if (status === 401 || status === 403) {
                setSaveState(SAVE_STATE.NO_AUTH);
            } else {
                setSaveState(SAVE_STATE.ERROR);
            }
        }
    };

    const handleSave = () => {
        if (!isLoggedIn) return;
        saveCard();
    };

    // 모달 로그인 성공 시: login()이 localStorage에 JWT를 즉시 기록하므로
    // 곧바로 저장 호출 시 인증 헤더가 포함된다 → "로그인하고 저장하기"를 한 번에 처리
    const handleLoginSuccess = () => {
        setLoginModalOpen(false);
        autoSavingRef.current = true;
        saveCard();
    };

    const saveBtnLabel = {
        [SAVE_STATE.CHECKING]: '확인 중...',
        [SAVE_STATE.IDLE]:     '내 명함함에 추가',
        [SAVE_STATE.LOADING]:  '저장 중...',
        [SAVE_STATE.SAVED]:    '✓ 저장 완료',
        [SAVE_STATE.ALREADY]:  '✓ 이미 저장됨',
        [SAVE_STATE.NO_AUTH]:  '로그인하고 저장하기 →',
        [SAVE_STATE.ERROR]:    '다시 시도',
    };

    const isNoAuth   = saveState === SAVE_STATE.NO_AUTH;
    const isDisabled = [SAVE_STATE.CHECKING, SAVE_STATE.LOADING, SAVE_STATE.SAVED, SAVE_STATE.ALREADY].includes(saveState);
    const isDone     = saveState === SAVE_STATE.SAVED || saveState === SAVE_STATE.ALREADY;

    return (
        <div style={{ minHeight: '100vh', background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
            <div style={{ background: '#fff', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', padding: '32px', maxWidth: '480px', width: '100%' }}>
                <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                    <h1 style={{ fontSize: '20px', fontWeight: '700', color: '#111827', margin: 0 }}>명함 정보</h1>
                </div>

                {isLoading && (
                    <div style={{ textAlign: 'center', color: '#9ca3af', padding: '40px 0' }}>불러오는 중...</div>
                )}

                {isError && (
                    <div style={{ textAlign: 'center', padding: '40px 0' }}>
                        <p style={{ color: '#ef4444', fontWeight: '600', fontSize: '16px', marginBottom: '8px' }}>링크를 찾을 수 없습니다.</p>
                        <p style={{ color: '#9ca3af', fontSize: '14px' }}>이 링크는 만료되었거나 삭제된 링크입니다.</p>
                    </div>
                )}

                {!isLoading && !isError && data && (
                    <>
                        {imageUrl && (
                            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                                <img
                                    src={imageUrl}
                                    alt="명함"
                                    style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain', borderRadius: '8px', border: '1px solid #e5e7eb' }}
                                />
                            </div>
                        )}

                        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '24px' }}>
                            <tbody>
                                {FIELD_LABELS.map(({ key, label }) =>
                                    fields[key] ? (
                                        <tr key={key} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                            <td style={{ padding: '10px 8px', fontSize: '13px', color: '#6b7280', width: '80px', fontWeight: '500' }}>{label}</td>
                                            <td style={{ padding: '10px 8px', fontSize: '14px', color: '#111827' }}>{fields[key]}</td>
                                        </tr>
                                    ) : null
                                )}
                            </tbody>
                        </table>

                        <div style={{ textAlign: 'center' }}>
                            {isDone && isLoggedIn ? (
                                <>
                                    <p style={{ margin: '0 0 14px', fontSize: '14px', fontWeight: '600', color: '#166534' }}>
                                        ✓ 내 명함함에 저장되었습니다
                                    </p>
                                    <Link
                                        to="/biz/card-list"
                                        style={{
                                            display: 'inline-block',
                                            padding: '10px 24px',
                                            borderRadius: '6px',
                                            fontSize: '14px',
                                            fontWeight: '600',
                                            border: 'none',
                                            background: '#3b82f6',
                                            color: '#fff',
                                            textDecoration: 'none',
                                        }}
                                    >
                                        내 명함함 보기 →
                                    </Link>
                                </>
                            ) : (
                                <button
                                    type="button"
                                    onClick={isNoAuth ? handleLoginClick : handleSave}
                                    disabled={isDisabled}
                                    style={{
                                        padding: '10px 24px',
                                        borderRadius: '6px',
                                        fontSize: '14px',
                                        fontWeight: '600',
                                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                                        border: 'none',
                                        background: '#3b82f6',
                                        color: '#fff',
                                    }}
                                >
                                    {saveBtnLabel[saveState]}
                                </button>
                            )}

                            {saveState === SAVE_STATE.ERROR && (
                                <p style={{ marginTop: '10px', fontSize: '13px', color: '#ef4444' }}>
                                    저장에 실패했습니다. 다시 시도해주세요.
                                </p>
                            )}
                        </div>

                        <p style={{ marginTop: '24px', textAlign: 'center', fontSize: '11px', color: '#d1d5db' }}>ATOS 안전관리시스템</p>
                    </>
                )}
            </div>

            <LoginModal
                isOpen={loginModalOpen}
                onClose={() => setLoginModalOpen(false)}
                onLoginSuccess={handleLoginSuccess}
            />
        </div>
    );
}
